// Centralized error handling configuration for TellerWand
// This prevents repetitive errors and provides graceful fallbacks

import { getClientSession } from "@/services/auth/clientSession";
import { logSystemEvent } from "@/services/system/logsService";

export interface ErrorHandlingConfig {
  // Whether to enable database logging (disable for anonymous users)
  enableDatabaseLogging: boolean;

  // Whether to enable console fallback logging
  enableConsoleFallback: boolean;

  // Maximum retry attempts for failed operations
  maxRetries: number;

  // Retry delay in milliseconds
  retryDelay: number;

  // Whether to show user-facing error messages
  showUserErrors: boolean;

  // Whether to log errors to external services
  enableExternalLogging: boolean;
}

// Default configuration
export const defaultErrorConfig: ErrorHandlingConfig = {
  enableDatabaseLogging: true,
  enableConsoleFallback: true,
  maxRetries: 2,
  retryDelay: 1000,
  showUserErrors: false,
  enableExternalLogging: false,
};

// Get error handling configuration based on environment
/**
 * Synchronizes the error handling configuration based on user session status.
 * @example
 * sync().then(config => console.warn(config));
 * // { enableDatabaseLogging: false, showUserErrors: false }
 * @returns {Promise<ErrorHandlingConfig>} A promise that resolves to the error handling configuration.
 */
export const getErrorHandlingConfig = async (): Promise<ErrorHandlingConfig> => {
  const config = { ...defaultErrorConfig };

  // Disable database logging for anonymous users to prevent 401 errors
  try {
    const { data: session } = await getClientSession();
    if (!session?.session?.user) {
      config.enableDatabaseLogging = false;
      config.showUserErrors = false;
    }
  } catch (error) {
    console.error("Operation failed:", error);
    // If we can't check session, assume anonymous user
    config.enableDatabaseLogging = false;
    config.showUserErrors = false;
  }

  return config;
};

/**
 * Is Email Local Char.
 */
function isEmailLocalChar(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return (
    (code >= 48 && code <= 57) ||
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    char === "." ||
    char === "_" ||
    char === "%" ||
    char === "+" ||
    char === "-"
  );
}

function isEmailDomainChar(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return (
    (code >= 48 && code <= 57) ||
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    char === "." ||
    char === "-"
  );
}

function isAlphaTld(tld: string): boolean {
  return (
    tld.length >= 2 &&
    [...tld].every((char) => {
      const code = char.codePointAt(0) ?? 0;
      return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
    })
  );
}

/**
 * Redact Email Addresses.
 */
function redactEmailAddresses(text: string): string {
  let result = "";
  let index = 0;

  while (index < text.length) {
    const atIndex = text.indexOf("@", index);
    if (atIndex === -1) {
      result += text.slice(index);
      break;
    }

    let localStart = atIndex;
    while (localStart > index && isEmailLocalChar(text[localStart - 1])) {
      localStart -= 1;
    }

    let domainEnd = atIndex + 1;
    while (domainEnd < text.length && isEmailDomainChar(text[domainEnd])) {
      domainEnd += 1;
    }

    const domain = text.slice(atIndex + 1, domainEnd);
    const lastDot = domain.lastIndexOf(".");
    const tld = lastDot >= 0 ? domain.slice(lastDot + 1) : "";

    if (localStart < atIndex && lastDot > 0 && isAlphaTld(tld)) {
      result += text.slice(index, localStart);
      result += "[REDACTED_EMAIL]";
      index = domainEnd;
      continue;
    }

    result += text.slice(index, atIndex + 1);
    index = atIndex + 1;
  }

  return result;
}

// Centralized error handler
/**
 * Handles synchronization of error events by logging and optionally notifying user.
 * @example
 * sync(new Error('Sample error message'), 'sampleContext', { enableConsoleFallback: true })
 * // Console will log a warning and may also log to a database depending on config
 * @param {unknown} error - The error to be synced, could be an instance of Error or any unknown type.
 * @param {string} context - The context or component name where the error occurred.
 * @param {Partial<ErrorHandlingConfig>} [config={}] - Optional config to customize error handling behavior.
 * @returns {Promise<void>} Resolves when error handling operations are complete.
 */
export const handleError = async (
  error: unknown,
  context: string,
  config: Partial<ErrorHandlingConfig> = {}
): Promise<void> => {
  const errorConfig = { ...(await getErrorHandlingConfig()), ...config };

  let errorMessage = "";
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === "string") {
    errorMessage = error;
  }

  // Sanitize error messages to remove database URLs and sensitive information
  errorMessage = redactEmailAddresses(
    errorMessage
      .replace(/https?:\/\/[a-zA-Z0-9.-]+\.supabase\.co[^\s]*/g, "[REDACTED_DB_URL]")
      .replace(/hkwcvidfamdifbdmyvej\.supabase\.co/g, "[REDACTED_DB]")
  ).replace(/Bearer\s+[A-Za-z0-9._-]+/g, "Bearer [REDACTED_TOKEN]");

  // Always log to console for debugging
  if (errorConfig.enableConsoleFallback) {
    console.warn("[", context, "] Error handled gracefully:", {
      message: errorMessage,
      context,
      timestamp: new Date().toISOString(),
      // Don't log userAgent or URL in production to reduce exposure
    });
  }

  // Only log to database if enabled and user is authenticated
  if (errorConfig.enableDatabaseLogging) {
    // This will be handled by the logsService with proper error handling
    await logSystemEvent({
      level: "error",
      component: context,
      message: errorMessage,
      service: "frontend",
      context: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      },
    });
  }

  // Show user-facing error if enabled
  if (errorConfig.showUserErrors) {
    // You can implement user notification here
    // For now, we'll just log it
    if (import.meta.env.DEV) {
      console.warn(`User-facing error in ${context}: ${errorMessage}`);
    }
  }
};
